/**
 * Notification Context
 *
 * Uses the unified EventStream for real-time notifications.
 * The EventStreamProvider handles the SSE connection.
 * This context manages notification state and API calls.
 */

import { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useEventStreamContext, CHANNELS } from "context/EventStreamContext";
import { useAuth } from "context/AuthContext";
import useApi from "hooks/useApi";
import { endpoints } from "config/api";

const NotificationContext = createContext(null);
NotificationContext.displayName = "NotificationContext";

export function NotificationProvider({ children }) {
  const { currentUser } = useAuth();
  const { get, patch } = useApi();
  const { connected: streamConnected, subscribe } = useEventStreamContext();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use refs to avoid stale closures in callbacks
  const getRef = useRef(get);
  const currentUserRef = useRef(currentUser);
  getRef.current = get;
  currentUserRef.current = currentUser;

  // Track if initial fetch has been done to avoid duplicate fetches
  const initialFetchDoneRef = useRef(false);

  // Fetch notifications via REST (initial load)
  const fetchNotifications = useCallback(async () => {
    if (!currentUserRef.current) return;
    try {
      setLoading(true);
      const data = await getRef.current(endpoints.notifications.base);
      setNotifications(data.notifications || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!currentUserRef.current) return;
    try {
      const data = await getRef.current(endpoints.notifications.unread);
      setUnreadCount(data.count || 0);
    } catch (err) {
      // silently ignore
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(
    async (notificationId) => {
      try {
        await patch(endpoints.notifications.markRead(notificationId));
        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        // silently ignore
      }
    },
    [patch]
  );

  // Refresh all notification data
  const refresh = useCallback(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  // Handle incoming notification events from EventStream
  const handleNotificationEvent = useCallback((event) => {
    if (event.type === "notification") {
      const notification = event.data;
      setNotifications((prev) => [notification, ...prev]);
      if (!notification.read) {
        setUnreadCount((prev) => prev + 1);
      }
    } else if (event.type === "snapshot") {
      // Full notification list refresh
      if (Array.isArray(event.data)) {
        setNotifications(event.data);
        setUnreadCount(event.data.filter((n) => !n.read).length);
      }
    }
  }, []);

  // Reset when user changes
  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      initialFetchDoneRef.current = false;
      return;
    }

    // Initial fetch only once per user session
    if (!initialFetchDoneRef.current) {
      initialFetchDoneRef.current = true;
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [currentUser, fetchNotifications, fetchUnreadCount]);

  // Subscribe to notifications channel (separate effect to avoid re-subscribing on fetch changes)
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = subscribe(CHANNELS.NOTIFICATIONS, handleNotificationEvent);
    return () => {
      unsubscribe();
    };
  }, [currentUser, subscribe, handleNotificationEvent]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      connected: streamConnected,
      loading,
      error,
      markAsRead,
      refresh,
    }),
    [notifications, unreadCount, streamConnected, loading, error, markAsRead, refresh]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useGlobalNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useGlobalNotifications must be used within NotificationProvider");
  }
  return context;
}

export default NotificationContext;
